import React, { useEffect, useState } from "react";
import { ErrorLog, ErrorLogFilters, getErrorLogs } from "../services/errorLogApi";

const severityStyles: Record<ErrorLog["severity"], string> = {
  critical: "bg-red-100 text-red-800 border-red-300",
  error: "bg-orange-100 text-orange-800 border-orange-300",
  warning: "bg-yellow-100 text-yellow-800 border-yellow-300",
};

type Pagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

const DEFAULT_FILTERS: ErrorLogFilters = {
  page: 1,
  limit: 50,
};

const ErrorMonitoring: React.FC = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedError, setExpandedError] = useState<string | null>(null);
  const [copiedError, setCopiedError] = useState<string | null>(null);

  const fetchErrors = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { errors: fetchedErrors, pagination: fetchedPagination } =
        await getErrorLogs(DEFAULT_FILTERS);
      setErrors(fetchedErrors);
      setPagination(fetchedPagination);
    } catch (error) {
      console.error("Failed to fetch error logs:", error);
      setLoadError("Unable to load error logs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyErrorDetails = async (error: ErrorLog) => {
    const details = {
      message: error.message,
      type: error.type,
      source: error.source,
      severity: error.severity,
      timestamp: new Date(error.createdAt).toLocaleString(),
      stack: error.stack,
      context: error.context,
    };

    const textToCopy = `Error Details
================
Message: ${details.message}
Type: ${details.type}
Source: ${details.source}
Severity: ${details.severity}
Timestamp: ${details.timestamp}

${details.stack ? `Stack Trace:\n${details.stack}\n\n` : ""}${details.context ? `Context:\n${JSON.stringify(details.context, null, 2)}` : ""}`;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedError(error.id);
      setTimeout(() => setCopiedError(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  useEffect(() => {
    void fetchErrors();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="bg-white shadow-sm rounded-lg p-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Error logs</h1>
            <p className="text-sm text-gray-600">Recent backend and frontend errors.</p>
          </div>
          <button
            type="button"
            onClick={() => void fetchErrors()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {loadError && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-md p-3">
            {loadError}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-3 bg-gray-50 border-b flex items-center justify-between text-sm text-gray-700">
            <span className="font-medium">{pagination.total} errors</span>
            <span>Showing latest {pagination.limit}</span>
          </div>

          {loading ? (
            <div className="p-10 text-center text-gray-500 text-sm">Loading errors...</div>
          ) : errors.length === 0 ? (
            <div className="p-10 text-center text-gray-500 text-sm">No errors found</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {errors.map((error) => (
                <div key={error.id} className="p-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <span
                      className={`px-2 py-1 text-[11px] font-semibold rounded border ${
                        severityStyles[error.severity]
                      }`}
                    >
                      {error.severity.toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{error.message}</p>
                      <p className="text-xs text-gray-600">
                        {error.type} • {error.source} • {new Date(error.createdAt).toLocaleString()}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        {(error.stack || error.context) && (
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:text-blue-700"
                            onClick={() =>
                              setExpandedError((current) => (current === error.id ? null : error.id))
                            }
                          >
                            {expandedError === error.id ? "Hide details" : "View details"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800"
                          onClick={() => copyErrorDetails(error)}
                          title="Copy error details"
                        >
                          {copiedError === error.id ? (
                            <>
                              <svg
                                className="w-3.5 h-3.5 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <span className="text-green-600">Copied!</span>
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {expandedError === error.id && (
                    <div className="mt-3 space-y-3">
                      {error.stack && (
                        <div>
                          <div className="text-xs font-semibold text-gray-700 mb-1 flex items-center justify-between">
                            <span>Stack trace</span>
                            <button
                              type="button"
                              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(error.stack || "");
                                  setCopiedError(`${error.id}-stack`);
                                  setTimeout(() => setCopiedError(null), 2000);
                                } catch (err) {
                                  console.error("Failed to copy stack:", err);
                                }
                              }}
                              title="Copy stack trace"
                            >
                              {copiedError === `${error.id}-stack` ? (
                                <>
                                  <svg
                                    className="w-3 h-3 text-green-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  <span className="text-green-600">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                          <pre className="text-xs text-gray-800 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-3 overflow-x-auto">
                            {error.stack}
                          </pre>
                        </div>
                      )}
                      {error.context && (
                        <div>
                          <div className="text-xs font-semibold text-gray-700 mb-1 flex items-center justify-between">
                            <span>Context</span>
                            <button
                              type="button"
                              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(
                                    JSON.stringify(error.context, null, 2)
                                  );
                                  setCopiedError(`${error.id}-context`);
                                  setTimeout(() => setCopiedError(null), 2000);
                                } catch (err) {
                                  console.error("Failed to copy context:", err);
                                }
                              }}
                              title="Copy context"
                            >
                              {copiedError === `${error.id}-context` ? (
                                <>
                                  <svg
                                    className="w-3 h-3 text-green-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  <span className="text-green-600">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                          <pre className="text-xs text-gray-800 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-3 overflow-x-auto">
                            {JSON.stringify(error.context, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorMonitoring;
